import { useContext, useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import Loader from '../helpers/Loader'
import { JourneyGateContext } from '../../providers/JourneyGate'

const JOURNEY_STATUS_URL = '/umbraco/surface/journey/status'
// After a renewal is submitted the member sits in Current - Pending Approval while D365 runs an
// asynchronous flow that can take 1-2 minutes. The ONLY positive signal is the flip to Current -
// Pending Payment, which the status endpoint surfaces as a redirect to a different page (the invoice
// page); we poll for it and redirect the instant it is confirmed. If the status stays Current - Pending
// Approval (a genuine manual review — changed only by hand in D365), the window lapses and this page's
// content is revealed.
const POLL_INTERVAL_MS = 3000
const MAX_WAIT_MS = 180000

/**
 * Holding page shown when a member's application/renewal is under manual review.
 * Status: current-pending-approval in D365.
 */
function PendingApprovalPage({ title, bodyText, supportUrl }) {
  const [checking, setChecking] = useState(true)
  const { releaseGate } = useContext(JourneyGateContext)

  // Open the journey gate once the poll window resolves without a redirect, so the sibling
  // blocks JourneyGateProvider hid reappear together with this page's content. On a redirect
  // the gate intentionally stays closed until navigation replaces the page.
  useEffect(() => {
    if (!checking) releaseGate()
  }, [checking, releaseGate])

  useEffect(() => {
    let cancelled = false
    let timerId
    const here = window.location.pathname.replace(/\/$/, '')
    const deadline = Date.now() + MAX_WAIT_MS
    // Pass the current path so the endpoint uses the page-aware guard (ResolveJourneyRedirect), which
    // routes purely on path + live status. The status-only mapping is gated by the member's cached
    // CanAccessRenewal flag, which is stale right after submit and would return a null redirect for a
    // just-flipped Current - Pending Payment member — leaving this poll stuck.
    const statusUrl = `${JOURNEY_STATUS_URL}?currentPath=${encodeURIComponent(here)}`

    // Halt polling — clear any pending timer and stop scheduling further reads.
    const stop = () => {
      cancelled = true
      if (timerId) window.clearTimeout(timerId)
    }

    const scheduleNext = () => {
      if (Date.now() < deadline) {
        timerId = window.setTimeout(poll, POLL_INTERVAL_MS)
      } else if (!cancelled) {
        // Window lapsed with no flip — the manual-review case. Reveal this page's content.
        setChecking(false)
      }
    }

    const poll = () => {
      fetch(statusUrl, { credentials: 'same-origin' })
        .then((res) => (res.ok ? res.json() : null))
        .then((data) => {
          if (cancelled) return
          // A null payload is a transient/non-OK read — retry, never treated as a flip.
          if (!data) {
            scheduleNext()
            return
          }
          const target = (data.redirectUrl || '').replace(/\/$/, '')
          // The status has flipped to Current - Pending Payment the moment the endpoint routes us to
          // a different page (the invoice page). Halt polling immediately and continue the journey —
          // the live D365 read is eventually consistent and can flicker, so act on the first flip
          // rather than waiting for consecutive confirmations.
          if (target && target !== here) {
            stop()
            window.location.href = data.redirectUrl
            return
          }
          // Still Current - Pending Approval — keep waiting.
          scheduleNext()
        })
        .catch(() => {
          // Transient error — retry, never treated as a flip or as end-of-wait.
          if (!cancelled) scheduleNext()
        })
    }

    // First poll after one interval — give D365 a moment to flip the status.
    timerId = window.setTimeout(poll, POLL_INTERVAL_MS)
    return stop
  }, [])

  if (checking) {
    return (
      <div className="pending-approval-page container mx-auto px-5 py-16 lg:px-0">
        <div
          className="mx-auto flex max-w-2xl items-center justify-center gap-3 py-16"
          role="status"
          aria-live="polite"
        >
          <Loader />
          <span className="text-midnight-500 text-lg font-semibold">Please wait… Do not refresh or navigate away from this page.</span>
        </div>
      </div>
    )
  }

  if (!title && !bodyText) return null

  return (
    <div className="pending-approval-page container mx-auto px-5 py-16 lg:px-0">
      <div className="mx-auto max-w-2xl text-center">
        {title && <h1 className="text-midnight-500 mb-6 text-3xl font-semibold">{title}</h1>}
        {bodyText && <p className="text-grey mb-8 text-lg">{bodyText}</p>}
        {supportUrl && (
          <a href={supportUrl} className="btn btn--secondary">
            Contact support
          </a>
        )}
      </div>
    </div>
  )
}

PendingApprovalPage.propTypes = {
  title: PropTypes.string,
  bodyText: PropTypes.string,
  supportUrl: PropTypes.string,
}

export default PendingApprovalPage
